<script setup>
import { onMounted, ref, watch } from 'vue'
import { Loader } from '@googlemaps/js-api-loader'
import { GoogleMap, MarkerCluster, Polygon, InfoWindow } from 'vue3-google-map'
import { GridAlgorithm } from '@googlemaps/markerclusterer'
import debounce from 'lodash/debounce'
import gtfsApi from '@/Shared/Services/gtfs.api.js'
import Switch from '@/Shared/Components/Forms/Switch.vue'
import ImportantInformation from '@/Shared/Components/Sections/ImportantInformation.vue'
import AppLink from '@/Shared/Components/Links/AppLink.vue'
import BusStopMarker from '@/Public/Partials/MapMarker/BusStopMarker.vue'
import LiveBusMarker from '@/Public/Partials/MapMarker/LiveBusMarker.vue'

defineProps({
  showDisclaimer: {
    type: Boolean,
    default: false,
  },
})
const clusterOptions = { algorithm: new GridAlgorithm({ gridSize: 50 }) }

const loader = new Loader({
  apiKey: import.meta.env.VITE_GOOGLE_API_KEY,
  version: 'weekly',
  libraries: ['geometry', 'places'],
})

const map = ref(null)
const markers = ref([])
const busStopMarkers = ref([])
const loading = ref(false)
const apiPromise = loader.load()
const drtServices = ref({})
const polygonData = ref([])
const showDrtServiceArea = ref(false)
const hasAnyServices = ref(false)
const selectedMarkerId = ref(null)
const showBusStops =ref(false)

const center = ref({ lat: 51.735991288633066, lng: 0.46658261948208646 })

const essexBounds = ref({
  north: 52.117316,
  south: 51.419518,
  west: -0.119,
  east: 1.972765,
})

const onMapMove = debounce(() => {
  const newCenter = map.value.map.getCenter()
  const bounds = map.value.map.getBounds()
  gtfsApi.liveUpdatesNearest(newCenter.lat(), newCenter.lng(), 10).then((response) => {
    loading.value = false
    if (response.status === 200) {
      markers.value = response.data
    }
  })
  if (showBusStops.value) {
    gtfsApi.busStops(bounds.getNorthEast().toJSON(), bounds.getSouthWest().toJSON(), map.value.map.zoom).then( response => {
      busStopMarkers.value = response.data
    })
  }
}, 500)

const onMarkerSelected = (marker) => {
  if (marker.gtfs_db_route_id) {
    window.open(`/about-bus-services/explore-bus-options/route/${marker.gtfs_db_route_id}`)
  }
  if (marker.gtfs_db_route_id == null) {
    window.open(route('timetable.cities.index', { route_name: marker.route_short_name }))
  }
}

onMounted(() => {
  loading.value = true
  gtfsApi.drtServices().then((response) => {
    if (response.status === 200) {
      drtServices.value = response.data
      if (Object.keys(drtServices.value).length !== 0) {
        hasAnyServices.value = true
      }
      loading.value = false
    }
  })
})

watch(showDrtServiceArea, () => {
  polygonData.value = []
  if (showDrtServiceArea.value && hasAnyServices.value) {
    drtServices.value.forEach((service) => {
      polygonData.value.push({
        paths: service.coordinates,
        strokeColor: '#FF0000',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#FF0000',
        fillOpacity: 0.35,
        visible: true,
        clickable: true,
        name: service.name,
        showInfo: false,
        showInfoPosition: null,
        url: service.find_out_more_link,
      })
    })
  }
})

watch(showBusStops, (value) => {
  if (value) {
    /* re-retrieve the bus stops for the current map bounds */
    onMapMove()
  }
})

const showDrtInfo = (polygon, event) => {
  polygon.showInfoPosition = event.latLng
  polygon.showInfo = !polygon.showInfo
}

const zoomIn = (marker) => {
  const [swLng, swLat] = marker.south_west.replace(/[()]/g, '').split(', ').map(Number)
  const [neLng, neLat] = marker.north_east.replace(/[()]/g, '').split(', ').map(Number)

  // Create LatLng objects from the parsed coordinates
  const southWest = new google.maps.LatLng(swLat, swLng)
  const northEast = new google.maps.LatLng(neLat, neLng)

  // Create a LatLngBounds object and extend it with both corners
  const mapBounds = new google.maps.LatLngBounds()
  mapBounds.extend(southWest)
  mapBounds.extend(northEast)

  // Fit the map to the specified bounds
  map.value.map.fitBounds(mapBounds)
}

// Function to toggle the InfoWindow visibility
const toggleMarkerInfo = (marker) => {
  if (selectedMarkerId.value === marker.id) {
    selectedMarkerId.value = null
  } else {
    selectedMarkerId.value = marker.id
  }
}

const hideInfoWindow = () => {
  selectedMarkerId.value = null
}

</script>

<template>
  <div
    class="mb-4 float-right flex items-center md:py-0"
  >
    <span
      class="text-sm"
    >
      Bus stops
    </span>
    <Switch
      checkedClass='bg-green-500'
      v-model:checked="showBusStops"
      class="mx-2"
    />
    <span
      v-if="hasAnyServices"
    >
      <span
        class="text-sm"
      >
        DaRT area
      </span>

      <Switch
        v-model:checked="showDrtServiceArea"
        checkedClass='bg-green-500'
        class="mx-2"
      />
    </span>
  </div>
 
  <GoogleMap
    ref="map"
    :api-promise="apiPromise"
    :center="center"
    class="w-full h-[512px] my-4"
    :zoom="13"
    :restriction="{
      latLngBounds: essexBounds,
      strictBounds: true,
    }"
    :styles="[
      {
        featureType: 'poi.business',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }],
      },
      {
        featureType: 'transit.station.bus',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }],
      },
    ]"
    @bounds_changed="onMapMove"
  >
    <MarkerCluster :options="clusterOptions">
      <!-- live buse markers -->
      <LiveBusMarker
        v-for="marker in markers"
        :key="marker.vehicle_id"
        :marker="marker"
        @selected="onMarkerSelected"
      />
    </MarkerCluster>
    
    <!-- bus stop markers with server-side clustering -->
    
    <BusStopMarker
      v-if="showBusStops"
      v-for="marker in busStopMarkers"
      :key="'bs-' + marker.id"
      :marker="marker"
      :selectedMarkerId="selectedMarkerId"
      @close="hideInfoWindow"
      @toggle="toggleMarkerInfo"
      @zoom-in="zoomIn"
    />

    <div
      v-for="polygon in polygonData"
      :key="polygon.name"
    >
      <Polygon
        v-if="showDrtServiceArea"
        :options="polygon"
        @click="showDrtInfo(polygon, $event)"
      />
      <InfoWindow
        v-if="polygon.showInfo"
        :options="{ position: polygon.showInfoPosition }"
      >
        <div>
          <h3 class="text-lg font-bold">{{ polygon.name }}</h3>
          <p class="text-sm">
            <AppLink
              :href="polygon.url"
              target="_blank"
            >
              More Information
            </AppLink>
          </p>
        </div>
      </InfoWindow>
    </div>
  </GoogleMap>
  <ImportantInformation
    v-if="showDisclaimer"
    class="w-full"
  >
    <p>
      The live vehicle tracking data is derived from the
      <AppLink :href="'https://www.bus-data.dft.gov.uk/'">Bus Open Data Service (BODS).</AppLink>
      A Transport Provider’s tracking data may be interrupted for a number of reasons. If a live feed goes down,
      scheduled information can be accessed from the Discover Buses and Plan a Journey functionality from within our
      homepage.
    </p>
  </ImportantInformation>
</template>
